import { useState } from 'react';
import axios from 'axios';
import { Mail, Send, Clock, MessageSquare, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/contact`, form);
      setSent(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="contact-success">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-[#0A0A0A] mb-3">Message Sent!</h2>
          <p className="text-[#4B5563] mb-6">
            Thank you for reaching out. We'll get back to you within 24 hours.
          </p>
          <Button onClick={() => { setSent(false); setForm({ name: '', email: '', subject: '', message: '' }); }} className="bg-[#002FA7] hover:bg-[#001B63] rounded-sm">
            Send Another Message
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[70vh] py-16 md:py-24 px-6" data-testid="contact-page">
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
          {/* Left — Info */}
          <div>
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#002FA7] mb-4 block">CONTACT US</span>
            <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-[#0A0A0A] mb-6">
              Get in Touch
            </h1>
            <p className="text-[#4B5563] leading-relaxed mb-8">
              Have a question about our programs, need help with your subscription, or want to learn more? We're here to help.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#002FA7]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#002FA7]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0A0A0A] text-sm">Rikir Enterprises LLC</h3>
                  <p className="text-[#4B5563] text-sm">215 East Bay St, Ste 201k #3579</p>
                  <p className="text-[#4B5563] text-sm">Charleston, South Carolina 29401</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#002FA7]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#002FA7]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0A0A0A] text-sm">Email</h3>
                  <p className="text-[#4B5563] text-sm">acerikir1@gmail.com</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#002FA7]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-[#002FA7]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0A0A0A] text-sm">Response Time</h3>
                  <p className="text-[#4B5563] text-sm">Within 24 hours</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#002FA7]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-5 h-5 text-[#002FA7]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0A0A0A] text-sm">Live Sessions</h3>
                  <p className="text-[#4B5563] text-sm">Available for subscribers Mon-Sat</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right — Form */}
          <div>
            <form onSubmit={handleSubmit} className="bg-white border border-[#E5E7EB] p-8 rounded-sm space-y-5" data-testid="contact-form">
              <div>
                <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Name</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your full name"
                  className="rounded-sm"
                  data-testid="contact-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Email</label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="your@email.com"
                  className="rounded-sm"
                  data-testid="contact-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Subject</label>
                <Input
                  value={form.subject}
                  onChange={(e) => setForm({ ...form, subject: e.target.value })}
                  placeholder="What's this about?"
                  className="rounded-sm"
                  data-testid="contact-subject"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Message</label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="How can we help you?"
                  rows={5}
                  className="rounded-sm"
                  data-testid="contact-message"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#002FA7] hover:bg-[#001B63] rounded-sm py-5 font-bold"
                data-testid="contact-submit"
              >
                {loading ? 'Sending...' : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
